/**
 * Pailena · repli visuel de la description de collection (repris du thème Effleura).
 *
 * PRINCIPE SEO — la description complete est rendue par Liquid dans le HTML
 * initial. Ce composant ne charge, n'injecte et ne retire JAMAIS de contenu :
 * il se contente de contraindre la hauteur visible du conteneur en CSS.
 * Le texte reste integralement dans le DOM, lisible par les moteurs et par
 * les lecteurs d'ecran, meme replie.
 *
 * AMELIORATION PROGRESSIVE — le repli n'est applique qu'apres l'execution du
 * script (attribut data-enhanced). Sans JavaScript, la description s'affiche
 * en entier et aucun bouton n'apparait : jamais de contenu inaccessible.
 *
 * Le bouton n'apparait que si le contenu depasse reellement la hauteur
 * repliee : une description courte reste affichee telle quelle.
 */
if (!customElements.get('pailena-description-toggle')) {
  class PailenaDescriptionToggle extends HTMLElement {
    #resizeObserver = null;

    /*
     * Champ portant une fonction flechee, et non une methode privee : une
     * methode privee n'est pas assignable, donc impossible a lier puis a
     * retirer proprement dans disconnectedCallback.
     */
    #handleClick = () => {
      const willOpen = this.dataset.open !== 'true';

      if (willOpen) {
        this.dataset.open = 'true';
      } else {
        delete this.dataset.open;
      }

      this.toggleButton.setAttribute('aria-expanded', String(willOpen));
      this.labelEl.textContent = willOpen ? this.labelLess : this.labelMore;

      // Au repliage, si le haut du bloc est sorti de l'ecran, on l'y ramene :
      // sans cela l'utilisateur se retrouve projete plus bas dans la page.
      if (!willOpen && this.getBoundingClientRect().top < 0) {
        const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.scrollIntoView({ block: 'start', behavior: reduced ? 'auto' : 'smooth' });
      }
    };

    #handleResize = () => this.#evaluate();

    connectedCallback() {
      this.body = this.querySelector('[data-ccd-body]');
      this.toggleButton = this.querySelector('[data-ccd-toggle]');
      this.labelEl = this.querySelector('[data-ccd-label]');
      if (!this.body || !this.toggleButton || !this.labelEl) return;

      this.labelMore = this.toggleButton.dataset.labelMore || 'Voir plus';
      this.labelLess = this.toggleButton.dataset.labelLess || 'Voir moins';

      this.toggleButton.addEventListener('click', this.#handleClick);
      this.#evaluate();

      // La hauteur du contenu change avec la largeur (reflow du texte) :
      // on re-evalue si le composant est redimensionne.
      if ('ResizeObserver' in window) {
        this.#resizeObserver = new ResizeObserver(this.#handleResize);
        this.#resizeObserver.observe(this);
      }
    }

    disconnectedCallback() {
      this.toggleButton?.removeEventListener('click', this.#handleClick);
      this.#resizeObserver?.disconnect();
      this.#resizeObserver = null;
    }

    /** Hauteur repliee lue depuis la variable CSS posee par la section. */
    get #collapsedHeight() {
      const value = parseInt(getComputedStyle(this).getPropertyValue('--ccd-collapsed'), 10);
      return Number.isFinite(value) ? value : 200;
    }

    /**
     * scrollHeight donne toujours la hauteur REELLE du contenu, y compris
     * lorsque le conteneur est deja contraint par max-height + overflow.
     * La mesure reste donc valable en etat replie comme deplie.
     */
    #evaluate() {
      if (this.dataset.open === 'true') return;

      if (this.body.scrollHeight > this.#collapsedHeight + 24) {
        this.dataset.enhanced = 'true';
        this.toggleButton.hidden = false;
      } else {
        delete this.dataset.enhanced;
        this.toggleButton.hidden = true;
      }
    }
  }

  customElements.define('pailena-description-toggle', PailenaDescriptionToggle);
}
